<template>
  <b-modal
    :id="modalId"
    :title="$t('dispatchFleet.cameras.title', { team: teamName })"
    size="lg"
    centered
    hide-footer
    @shown="onShown"
    @hidden="onHidden"
  >
    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner small />
    </div>

    <template v-else>
      <b-alert
        v-if="error"
        show
        variant="warning"
        class="mb-1"
      >
        <div class="alert-body">
          {{ error }}
        </div>
      </b-alert>

      <p
        v-else-if="!cameras.length"
        class="text-muted mb-0"
      >
        {{ reasonText }}
      </p>

      <template v-else>
        <div
          v-for="cam in cameras"
          :key="cam.device_id"
          class="dfm-camera"
        >
          <div class="dfm-camera__head">
            <strong>{{ cam.name }}</strong>
            <span
              v-if="cam.online !== null"
              class="dfm-pill"
              :class="cam.online ? 'dfm-pill--on' : 'dfm-pill--off'"
            >{{ cam.online ? $t('dispatchFleet.cameras.online') : $t('dispatchFleet.cameras.offline') }}</span>
          </div>
          <div class="dfm-channels">
            <button
              v-for="ch in cam.channels"
              :key="ch.channel"
              type="button"
              class="dfm-channel"
              :class="{ 'dfm-channel--on': isSelected(cam, ch) }"
              @click="choose(cam, ch)"
            >
              <feather-icon
                :icon="ch.locked && !tokens[tokenKey(cam, ch)] ? 'LockIcon' : 'VideoIcon'"
                size="13"
                class="mr-25"
              />
              {{ ch.name }}
            </button>
          </div>
        </div>

        <!-- Canal bloqueado: pede as credenciais do responsável (as mesmas do Frotas). A senha não é guardada. -->
        <form
          v-if="unlocking"
          class="dfm-unlock"
          @submit.prevent="submitUnlock"
        >
          <div class="font-weight-bold mb-50">
            {{ $t('dispatchFleet.cameras.unlockTitle', { channel: unlocking.channel.name }) }}
          </div>
          <div class="form-row">
            <div class="col-md-6 mb-50">
              <input
                v-model="credentials.email"
                type="email"
                class="form-control form-control-sm"
                :placeholder="$t('dispatchFleet.cameras.email')"
                autocomplete="off"
                required
              >
            </div>
            <div class="col-md-6 mb-50">
              <input
                v-model="credentials.password"
                type="password"
                class="form-control form-control-sm"
                :placeholder="$t('dispatchFleet.cameras.password')"
                autocomplete="new-password"
                required
              >
            </div>
          </div>
          <b-button
            type="submit"
            size="sm"
            variant="primary"
            :disabled="unlockingBusy"
          >
            <b-spinner
              v-if="unlockingBusy"
              small
            />
            {{ $t('dispatchFleet.cameras.unlockButton') }}
          </b-button>
        </form>

        <div
          v-if="selected"
          class="dfm-player"
        >
          <video
            ref="video"
            controls
            autoplay
            muted
            playsinline
          />
          <div
            v-if="streamLoading"
            class="dfm-player__overlay"
          >
            <b-spinner small /> {{ $t('dispatchFleet.cameras.opening') }}
          </div>
          <div
            v-if="streamError"
            class="dfm-player__overlay dfm-player__overlay--error"
          >
            <div>{{ streamError }}</div>
            <b-button
              size="sm"
              variant="light"
              class="mt-50"
              @click="startPreview"
            >
              {{ $t('dispatchFleet.cameras.retry') }}
            </b-button>
          </div>
        </div>
        <small
          v-if="selected"
          class="text-muted d-block mt-50"
        >{{ $t('dispatchFleet.cameras.delayNote') }}</small>
      </template>
    </template>
  </b-modal>
</template>

<script>
import Hls from 'hls.js'
import ApiErrorMixin from '@/mixins/ApiErrorMixin'

// A URL do Frotas vale ~15 min: renova um pouco antes para o vídeo não cair no meio de um atendimento.
const RENEW_MARGIN_SECONDS = 60

export default {
  name: 'DispatchCamerasModal',

  mixins: [ApiErrorMixin],

  props: {
    dispatchId: { type: [Number, String], required: true },
    teamName: { type: String, default: '' },
  },

  data() {
    return {
      loading: false,
      error: null,
      cameras: [],
      reason: null,
      selected: null, // { cam, ch }
      unlocking: null, // { cam, channel } aguardando credenciais
      unlockingBusy: false,
      credentials: { email: '', password: '' },
      tokens: {}, // `${device}:${canal}` => token de desbloqueio (só em memória)
      streamLoading: false,
      streamError: null,
      hls: null,
      renewTimer: null,
    }
  },

  computed: {
    modalId() {
      return `dispatch-cameras-${this.dispatchId}`
    },
    reasonText() {
      const key = { inactive: 'inactive', no_code: 'noCode', ambiguous: 'ambiguous' }[this.reason] || 'none'

      return this.$t(`dispatchFleet.cameras.reason.${key}`)
    },
  },

  beforeDestroy() {
    this.stop()
  },

  methods: {
    show() {
      this.$bvModal.show(this.modalId)
    },

    async onShown() {
      this.loading = true
      this.error = null
      try {
        const res = await this.$http.get(`${this.$store.state.api}occurrence_dispatches/${this.dispatchId}/cameras`)
        const data = res.data.data || {}
        this.cameras = data.cameras || []
        this.reason = data.reason || null
      } catch (e) {
        this.cameras = []
        this.error = this.apiErrorText(e, this.$t('dispatchFleet.cameras.loadError'))
      } finally {
        this.loading = false
      }
    },

    onHidden() {
      this.stop()
      this.selected = null
      this.unlocking = null
      this.credentials = { email: '', password: '' }
      this.tokens = {}
    },

    tokenKey(cam, ch) {
      return `${cam.device_id}:${ch.channel}`
    },

    isSelected(cam, ch) {
      return !!this.selected && this.selected.cam.device_id === cam.device_id && this.selected.ch.channel === ch.channel
    },

    choose(cam, ch) {
      this.stop()
      this.streamError = null

      if (ch.locked && !this.tokens[this.tokenKey(cam, ch)]) {
        this.selected = null
        this.unlocking = { cam, channel: ch }
        this.credentials = { email: '', password: '' }

        return
      }

      this.unlocking = null
      this.selected = { cam, ch }
      this.$nextTick(this.startPreview)
    },

    async submitUnlock() {
      const { cam, channel } = this.unlocking
      this.unlockingBusy = true
      try {
        const res = await this.$http.post(`${this.$store.state.api}occurrence_dispatches/${this.dispatchId}/cameras/unlock`, {
          device_id: cam.device_id,
          channel: channel.channel,
          email: this.credentials.email,
          password: this.credentials.password,
        })
        this.$set(this.tokens, this.tokenKey(cam, channel), res.data.data.token || 'sem-bloqueio')
        this.credentials = { email: '', password: '' } // a senha não fica na tela nem na memória
        this.unlocking = null
        this.selected = { cam, ch: channel }
        this.$nextTick(this.startPreview)
      } catch (e) {
        this.credentials.password = ''
        this.$message(this.$t('common.error'), this.apiErrorText(e, this.$t('dispatchFleet.cameras.unlockError')), 'error')
      } finally {
        this.unlockingBusy = false
      }
    },

    async startPreview() {
      if (!this.selected) return

      const { cam, ch } = this.selected
      this.stop()
      this.streamLoading = true
      this.streamError = null

      try {
        const token = this.tokens[this.tokenKey(cam, ch)]
        const res = await this.$http.post(`${this.$store.state.api}occurrence_dispatches/${this.dispatchId}/cameras/preview`, {
          device_id: cam.device_id,
          channel: ch.channel,
          unlock_token: token && token !== 'sem-bloqueio' ? token : undefined,
        })
        const { url, expires_in: expiresIn } = res.data.data

        this.attach(url)
        // Renova antes de a URL expirar.
        this.renewTimer = setTimeout(this.startPreview, Math.max(30, (expiresIn || 900) - RENEW_MARGIN_SECONDS) * 1000)
      } catch (e) {
        const status = e.response && e.response.status
        if (status === 423) {
          // O desbloqueio expirou: pede as credenciais de novo.
          this.$delete(this.tokens, this.tokenKey(cam, ch))
          this.selected = null
          this.unlocking = { cam, channel: ch }
        } else {
          this.streamError = this.apiErrorText(e, this.$t('dispatchFleet.cameras.playerError'))
        }
      } finally {
        this.streamLoading = false
      }
    },

    attach(url) {
      const video = this.$refs.video
      if (!video) return

      if (Hls.isSupported()) {
        this.hls = new Hls({ lowLatencyMode: true, liveSyncDurationCount: 3 })
        this.hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) this.streamError = this.$t('dispatchFleet.cameras.playerError')
        })
        this.hls.loadSource(url)
        this.hls.attachMedia(video)
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = url
      } else {
        this.streamError = this.$t('dispatchFleet.cameras.unsupported')
      }
    },

    stop() {
      clearTimeout(this.renewTimer)
      this.renewTimer = null
      if (this.hls) {
        this.hls.destroy()
        this.hls = null
      }
      const video = this.$refs.video
      if (video) {
        video.removeAttribute('src')
        video.load()
      }
    },
  },
}
</script>

<style scoped>
.dfm-camera {
  margin-bottom: 14px;
}

.dfm-camera__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.dfm-pill {
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.dfm-pill--on {
  color: #1d7a4a;
  background: rgba(40, 199, 111, 0.16);
}

.dfm-pill--off {
  color: #8a6d00;
  background: rgba(255, 159, 67, 0.2);
}

.dfm-channels {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.dfm-channel {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border: 1px solid #d8d6de;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  font-size: 0.85rem;
  cursor: pointer;
}

.dfm-channel:hover {
  border-color: #7367f0;
}

.dfm-channel--on {
  border-color: #7367f0;
  background: #7367f0;
  color: #fff;
}

.dfm-unlock {
  margin: 12px 0;
  padding: 12px;
  border: 1px solid #d8d6de;
  border-radius: 8px;
}

.dfm-player {
  position: relative;
  margin-top: 12px;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
}

.dfm-player video {
  display: block;
  width: 100%;
  max-height: 60vh;
}

.dfm-player__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
  text-align: center;
  padding: 12px;
}

.dfm-player__overlay--error {
  background: rgba(120, 20, 30, 0.8);
}

.dark-layout .dfm-channel,
.dark-layout .dfm-unlock {
  border-color: #3b4253;
}
</style>
